import { DialogChat } from "../../../types/DialogChat";
import { getTranslation } from "../../../utils/getTranslation";

export const GLOBAL_CHAT_TYPING_START_THROTTLE_MS = 7000;
export const GLOBAL_CHAT_TYPING_IDLE_TIMEOUT_MS = 10000;
export const GLOBAL_CHAT_REMOTE_TYPING_TIMEOUT_MS = 12000;

export const shouldSendTypingStart = ({
  text,
  isFocused,
  canWrite,
  isTyping,
  now,
  lastStartedAt,
  throttleMs = GLOBAL_CHAT_TYPING_START_THROTTLE_MS,
}: {
  text: string;
  isFocused: boolean;
  canWrite: boolean;
  isTyping: boolean;
  now: number;
  lastStartedAt: number;
  throttleMs?: number;
}) =>
  Boolean(text.trim()) &&
  isFocused &&
  canWrite &&
  (!isTyping || now - lastStartedAt >= throttleMs);

export const getRemoteTypingUserIds = ({
  chat,
  typingUserIds,
  currentUserId,
}: {
  chat?: Pick<DialogChat, "members"> | null;
  typingUserIds?: string[];
  currentUserId?: string;
}) => {
  const memberIds = new Set((chat?.members || []).map((member) => member._id));

  return Array.from(new Set(typingUserIds || [])).filter(
    (userId) => userId !== currentUserId && memberIds.has(userId),
  );
};

export const getTypingIndicatorText = ({
  chat,
  typingUserIds,
  currentUserId,
  selectedLanguage,
}: {
  chat?: Pick<DialogChat, "members"> | null;
  typingUserIds?: string[];
  currentUserId?: string;
  selectedLanguage?: string;
}) => {
  const remoteTypingUserIds = getRemoteTypingUserIds({
    chat,
    typingUserIds,
    currentUserId,
  });

  if (remoteTypingUserIds.length === 0 || !chat) {
    return "";
  }

  const names = remoteTypingUserIds.map((userId) => {
    const member = chat.members.find((item) => item._id === userId);
    const fullname = [
      getTranslation(member?.name, selectedLanguage),
      getTranslation(member?.surname, selectedLanguage),
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    return fullname || "Someone";
  });

  if (names.length === 1) {
    return `${names[0]} is typing`;
  }

  if (names.length === 2) {
    return `${names[0]} and ${names[1]} are typing`;
  }

  return `${names[0]} and ${names.length - 1} others are typing`;
};
